import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";

type Variant = "primary" | "secondary" | "ghost";
type Size = "md" | "lg";

type BaseProps = {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
};

const BASE =
  "inline-flex items-center justify-center gap-2 font-semibold rounded-full " +
  "transition-[background-color,transform,box-shadow,color,border-color] duration-150 " +
  "active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "focus-visible:outline-brand-500 select-none whitespace-nowrap " +
  "disabled:pointer-events-none disabled:opacity-80 disabled:active:scale-100";

const SIZES: Record<Size, string> = {
  md: "px-6 py-3 text-sm",
  lg: "px-7 py-3.5 text-base",
};

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-brand-500 text-white shadow-md hover:brightness-105 hover:shadow-xl",
  secondary:
    "bg-transparent border border-border-strong dark:border-border-base " +
    "text-text-primary hover:bg-bg-subtle dark:hover:bg-bg-muted",
  ghost:
    "bg-transparent text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950/60",
};

function classes(
  variant: Variant,
  size: Size,
  className: string | undefined,
): string {
  return `${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${className ?? ""}`.trim();
}

type ButtonAsButtonProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
    as?: "button";
  };

type ButtonAsAnchorProps = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "children"> & {
    as: "a";
    href: string;
  };

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

export function Button(props: ButtonProps) {
  const {
    variant = "primary",
    size = "md",
    className,
    children,
    ...rest
  } = props;

  if (props.as === "a") {
    const anchorRest = rest as AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a {...anchorRest} className={classes(variant, size, className)}>
        {children}
      </a>
    );
  }

  const buttonRest = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      {...buttonRest}
      type={buttonRest.type ?? "button"}
      className={classes(variant, size, className)}
    >
      {children}
    </button>
  );
}
